import { Badge, Button, Card, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../../i18n/I18nProvider';
import { formatDate, formatDateTime } from '../../../../lib/format';
import { useIsOrgAdmin } from '../../../../app/components/RoleGate';
import { useContactFields } from './FieldsTab';
import { fieldLabel, shown, undoable, useContactChanges, useUndoChange, whoOf } from '../ContactChanges';

/**
 * Every change to this contact's details, newest first (4 October 2026): the
 * field, what it was and what it became, who or what changed it, whether the
 * customer confirmed or said no, and when. Modelled on the Consent tab, which
 * answers the same kind of question for opt-ins.
 */
export function HistoryTab({ contactId, onUndone }: { contactId: string; onUndone: (field: string, value: string | null) => void }) {
  const t = useT();
  const isAdmin = useIsOrgAdmin();
  const { defs } = useContactFields();
  const { data, isPending, error } = useContactChanges(contactId);
  const { undo, pendingId, refusal } = useUndoChange(contactId, onUndone);
  const rows = data?.data ?? [];

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('contacts.view.tabs.history')}</Title>
        <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.changes.history.hint')}</Text>

        {error && <Alert color="error" role="alert">{t('contacts.changes.error.load')}</Alert>}
        {refusal && <Alert color="warning" role="alert">{refusal}</Alert>}

        {isPending && <Stack gap="xs">{[0, 1, 2].map((i) => <Skeleton key={i} height={48} />)}</Stack>}

        {!isPending && !error && rows.length === 0 && (
          <EmptyState title={t('contacts.changes.history.empty')} description={t('contacts.changes.history.empty.hint')} />
        )}

        {rows.map((c) => (
          <div key={c.id} className="rr-contact-changes__row" data-testid="contact-change-row">
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6} wrap="wrap">
                <Text role="bodySmall" fw={600}>{fieldLabel(t, c.field, defs)}</Text>
                {c.outcome === 'kept' && <Badge size="xs" variant="light" color="gray">{t('contacts.changes.badge.declined')}</Badge>}
                {c.outcome !== 'kept' && c.confirmed_by === 'customer' && <Badge size="xs" variant="light" color="green">{t('contacts.changes.badge.confirmed')}</Badge>}
                {c.reviewed_at == null && <Badge size="xs" variant="light" color="orange">{t('contacts.changes.badge.unreviewed')}</Badge>}
              </Group>
              <Text role="bodySmall">
                {c.outcome === 'kept'
                  ? t('contacts.changes.history.kept', { old: shown(t, c.old_value), new: shown(t, c.new_value) })
                  : t('contacts.changes.history.fromTo', { old: shown(t, c.old_value), new: shown(t, c.new_value) })}
              </Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {/* An undo already reads "Undo by ...". */}
                {c.source === 'undo' ? whoOf(t, c) : t('contacts.changes.history.by', { who: whoOf(t, c) })}
              </Text>
            </Stack>
            <Stack gap={6} align="flex-end">
              <Tooltip label={formatDateTime(c.occurred_at)} position="left">
                <span className="rr-contact-changes__when">{formatDate(c.occurred_at)}</span>
              </Tooltip>
              {undoable(c, rows, isAdmin) && (
                <Button size="xs" variant="default" loading={pendingId === c.id} onClick={() => undo(c.id)}>
                  {t('contacts.changes.undo')}
                </Button>
              )}
            </Stack>
          </div>
        ))}
      </Stack>
    </Card>
  );
}
